import { produce } from 'immer';
import { type SetRequired } from 'type-fest';

import {
  type CanvasElement,
  type CanvasElementId,
  type HasProps,
  type TitlePropsModel,
} from '@blaze-runtime/engine-domain';
import { type ConnectionId } from '@blaze-shared/api-contracts-domain';
import { convertDatasetColumnsToColumnInfos } from '@blaze-shared/schema-domain';
import { type Connection, isGetNode, type Workflow } from '@blaze-shared/workflow-domain';

import { hasProps } from '../../canvas-elements/element-model/element-props-guards';
import { type ResolvedLayoutInputCarrier } from '../models/ai.model';

import { type RelatedDatasetMap, type RelatedDetails, type RelatedElementWorkflow } from './models';

export interface WorkflowInputsAndConnections {
  inputs: Record<string, ConnectionId>;
  connections: Connection[];
}

export function mergeResolvedLayoutInputs(
  connectionsMap: Record<string, Connection>,
  resolvedLayoutInputs: ResolvedLayoutInputCarrier | undefined,
): WorkflowInputsAndConnections {
  const resolvedByInputName = new Map(
    Object.values(resolvedLayoutInputs ?? {}).map(({ inputName, connection }) => [
      inputName,
      connection,
    ]),
  );

  const inputs: Record<string, ConnectionId> = {};
  const connections: Connection[] = [];

  for (const [inputName, connection] of Object.entries(connectionsMap)) {
    if (resolvedByInputName.has(inputName)) {
      continue;
    }
    inputs[inputName] = connection.id;
    connections.push(connection);
  }

  for (const [inputName, connection] of resolvedByInputName) {
    inputs[inputName] = connection.id;
    connections.push(connection);
  }

  return { inputs, connections };
}

export function processWorkflow(
  workflow: Workflow,
  inputs: Record<string, ConnectionId>,
  optionalInputs: string[],
): Workflow {
  // Matches variables such as $$xxx
  const paramMatcher = /\$\$([\w]+)/g;

  return produce(workflow, (draft) => {
    // Override inputs
    draft.inputs = inputs;

    // Find conditions using optional inputs and mark them as optional
    draft.nodes
      .filter(isGetNode)
      .flatMap((node) => node.queryConditions)
      .filter((condition) => {
        const matchedParameterNames: string[] = [...condition.value.matchAll(paramMatcher)].map(
          (match) => match[1],
        );
        return matchedParameterNames.some((parameterName) =>
          optionalInputs.includes(parameterName),
        );
      })
      .forEach((condition) => (condition.isOptional = true));
  });
}

export function prepareRelatedDatasetMap(relatedDetailsList: RelatedDetails[]): RelatedDatasetMap {
  const datasetMap = new Map<string, CanvasElementId>();

  for (const details of relatedDetailsList) {
    if (!!details.datasetId && !datasetMap.has(details.datasetId)) {
      datasetMap.set(details.datasetId, details.element.id);
    }
  }

  return datasetMap;
}

export function prepareRelatedWorkflows(
  relatedDetailsList: RelatedDetails[],
): RelatedElementWorkflow[] {
  // TODO: unique values?

  return relatedDetailsList
    .filter((details): details is SetRequired<RelatedDetails, 'workflowId'> => !!details.workflowId)
    .map((details) => ({
      id: details.workflowId,
      name: getElementTitle(details.element),
      columns: convertDatasetColumnsToColumnInfos(details.columns),
    }));
}

function getElementTitle(element: CanvasElement): string | null {
  if (!hasElementTitleProps(element)) {
    return null;
  }
  const titleProps = element.props.content.title;

  return titleProps.enabled === false ? null : titleProps.label;
}

function hasElementTitleProps(
  element: CanvasElement,
): element is CanvasElement & HasProps<TitlePropsModel> {
  return hasProps(element) && 'content' in element.props && 'title' in element.props.content;
}
